import { useLocation, useHistory } from 'react-router-dom';
import { match } from 'path-to-regexp';
import { Realm } from 'further-types/global';
import { NoSubscriptionsInvestorLandingPage } from 'further-types/firm';
import { useWhiteLabellingContext } from 'contexts/WhiteLabellingContext';
import routes, {
  AUTH_GATE_COPY_PROFILE,
  AUTH_GATE_CHOOSE_ACCOUNT,
  INVESTMENT_OPPORTUNITIES,
  LP_VEHICLE,
} from 'routes/CommonRoutes';
import getUserType from 'utils/getUserType';
import { AuthGateDecision } from 'further-types/further-user';
import isInvestorOnboardingCompleteOrSkipped from 'helpers/user/isInvestorOnboardingCompleteOrSkipped';
import { useAuthClaims } from 'hooks/ui/useAuthClaims';
import { useFlag } from '@reflag/react-sdk';

const adviserOnlyPaths = routes
  .filter((route) => route.isAdviserOnlyRoute)
  .map((route) => match(route.path, { decode: decodeURIComponent }));
const investorOnlyPaths = routes
  .filter((route) => route.isInvestorOnlyRoute)
  .map((route) => match(route.path, { decode: decodeURIComponent }));
const organisationOnlyPaths = routes
  .filter((route) => route.isOrganisationOnlyRoute)
  .map((route) => match(route.path, { decode: decodeURIComponent }));
const lpPrivatePortalPaths = routes
  .filter(
    (route) =>
      route.isInvestorOnlyRoute &&
      route.restrictToInvestorRealm === Realm.Lp &&
      route.private === true,
  )
  .map((route) => match(route.path, { decode: decodeURIComponent }));

export const adviserHomepageRedirect = ({
  user,
  goTo,
  fromState,
  isAdminUserImpersonatingFurtherUser,
}) => {
  if (!user?.isOnboardingComplete && !isAdminUserImpersonatingFurtherUser) {
    goTo('/adviser-onboarding?step=1');
  } else {
    if (
      fromState?.pathname &&
      !investorOnlyPaths.some((match) => match(fromState.pathname)) &&
      !organisationOnlyPaths.some((match) => match(fromState.pathname))
    ) {
      const { pathname, search = '', hash = '' } = fromState;
      goTo(pathname + search + hash);
    } else {
      goTo('/adviser-dashboard');
    }
  }
};

export const organisationHomepageRedirect = ({ goTo, fromState }) => {
  if (
    fromState?.pathname &&
    !adviserOnlyPaths.some((match) => match(fromState.pathname)) &&
    !investorOnlyPaths.some((match) => match(fromState.pathname))
  ) {
    const { pathname, search = '', hash = '' } = fromState;
    goTo(pathname + search + hash);
  } else {
    goTo('/org-dashboard');
  }
};

export const investorHomepageRedirect = ({
  user,
  goTo,
  fromState,
  isSubdomain,
  noSubscriptionsInvestorLandingPage,
  dataRoomEnabled,
}) => {
  const { userIsLpInvestor } = getUserType(user);

  if (userIsLpInvestor) {
    if (
      fromState?.pathname &&
      lpPrivatePortalPaths.some((pathMatch) =>
        Boolean(pathMatch(fromState.pathname)),
      )
    ) {
      const { pathname, search = '', hash = '' } = fromState;
      goTo(pathname + search + hash);
    } else {
      goTo(LP_VEHICLE);
    }
  } else {
    if (!isInvestorOnboardingCompleteOrSkipped(isSubdomain, user)) {
      goTo('/onboarding');
    } else if (
      fromState?.pathname &&
      !adviserOnlyPaths.some((match) => match(fromState.pathname)) &&
      !organisationOnlyPaths.some((match) => match(fromState.pathname))
    ) {
      const { pathname, search = '', hash = '' } = fromState;
      goTo(pathname + search + hash);
    } else {
      const landOnInvestmentOpportunities =
        dataRoomEnabled &&
        !user.hasSubscriptions &&
        !!user.hasAvailableInvestmentOpportunities &&
        noSubscriptionsInvestorLandingPage ===
          NoSubscriptionsInvestorLandingPage.InvestmentOpportunitiesOrPortal;

      goTo(
        landOnInvestmentOpportunities ? INVESTMENT_OPPORTUNITIES : '/portal',
      );
    }
  }
};

const handleAuthGateDecision = ({ user, history, fromState }) => {
  const destination =
    user?.authGateDecision === AuthGateDecision.CopyProfile
      ? AUTH_GATE_COPY_PROFILE
      : AUTH_GATE_CHOOSE_ACCOUNT;

  history.push(destination, ...(fromState ? [{ from: fromState }] : []));
};

export const useHomepageRedirect = () => {
  const { isSubdomain, firm } = useWhiteLabellingContext();
  const history = useHistory();
  const location = useLocation();
  const { isAdminUserImpersonatingFurtherUser } = useAuthClaims();
  const { isEnabled: dataRoomEnabled } = useFlag('data-room-enabled');

  type PossibleState = {
    from?: {
      pathname: string;
      search: string;
      hash: string;
    };
  };
  const fromState = (location?.state as PossibleState)?.from;

  const goTo = (to: string) => {
    if (`${location?.pathname}${location?.search}${location?.hash}` !== to) {
      history.push(to);
    }
  };
  const goToClearFromState = (to: string) => {
    if (`${location?.pathname}${location?.search}${location?.hash}` !== to) {
      history.push(to, { state: {} });
    }
  };

  return function ({ user }) {
    const { userIsAdviser, userIsOrg, userIsInvestor } = getUserType(user);
    const isLoggedIn = user?.isAuthenticated;

    if (isLoggedIn) {
      if (user?.authGateDecision) {
        handleAuthGateDecision({ history, user, fromState });
      } else if (userIsAdviser) {
        adviserHomepageRedirect({
          goTo: goToClearFromState,
          user,
          fromState,
          isAdminUserImpersonatingFurtherUser,
        });
      } else if (userIsOrg) {
        organisationHomepageRedirect({ goTo: goToClearFromState, fromState });
      } else if (userIsInvestor) {
        investorHomepageRedirect({
          goTo: goToClearFromState,
          user,
          fromState,
          isSubdomain,
          noSubscriptionsInvestorLandingPage:
            firm?.whiteLabelConfig?.noSubscriptionsInvestorLandingPage,
          dataRoomEnabled,
        });
      }
    } else {
      if (isSubdomain) {
        goTo(`/login${location.search}`);
      } else {
        goTo('/');
      }
    }
  };
};
